import moment from 'moment/moment'

import { IconName, IconNameAndCategoryString } from '../components/Icon'
import { PSApplication } from '../components/app/PSApplication'
import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { CacheFactory } from './CacheFactory'
import { Industry } from './Industry'
import { FKType } from './SQLBuilder'
import { Subaccount } from './Subaccount'
import { User } from './User'

type FeatureConstructorArgs = ConstructorArgs<() => Feature>

export enum FEATURE {
    FEATURE_GROUP,
    FEATURE_CLASS,
    FEATURE_PACKAGE,
    FEATURE_ESTIMATE,
    FEATURE_SUBSCRIPTION,
    FEATURE_CONTRACT,
    FEATURE_WIDGET,
    FEATURE_FIELD,
    FEATURE_RECORD,
    FEATURE_GIFT,
    FEATURE_DISCOUNT,
    FEATURE_SALESTAX,
    FEATURE_LINK,
    FEATURE_CHAT,
    FEATURE_LEAD,
    FEATURE_MARKETING,
    FEATURE_TASK,
    FEATURE_DRIP,
    FEATURE_POST,
    FEATURE_TRIGGER,
    FEATURE_BILLING,
    FEATURE_SURCHARGE,
    FEATURE_STAFFING,
    FEATURE_SUBACCOUNT,
    FEATURE_RESERVATION,
    FEATURE_SERVICES,
    FEATURE_RECURRING,
    FEATURE_GRATUITY,
    FEATURE_TEMPLATE,
    FEATURE_REMINDER,
    FEATURE_PRODUCT,
    FEATURE_BENCHMARK,
    FEATURE_READER,
    FEATURE_PAYMENT,
    FEATURE_INVOICE,
    FEATURE_BOOKING,
    FEATURE_ITEMIZE,
    FEATURE_PAYPAL,
    FEATURE_CATEGORY,
    FEATURE_BNPL,
    FEATURE_MARK,
    FEATURE_ROLLUP,
    FEATURE_ROYALTY,
    FEATURE_RESOURCE,
}

export class Feature extends BaseRecord {
    readonly recordType = 'feature'

    seqnum?: number
    name?: string
    memo?: string
    pitch?: string
    parent?: string
    requiresPlus?: boolean
    requiresPremium?: boolean
    cancelledDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    constructor(data?: Partial<FeatureConstructorArgs>) {
        super(data)
        if (!data) {
            return
        }

        this.name = (() => {
            if (this.id === 'overview') return 'Overview'
            if (this.id === 'setup') return 'Get started'
            if (this.id === 'reports') return 'Smart reports'
            if (this.id === 'imports') return 'Import contracts'
            if (this.id === 'fields') return 'Client fields'
            if (this.id === 'documents') return 'Documents'
            if (this.id === 'cancellations') return 'Cancellations'
            if (this.id === 'conference') return 'Video conference'
            if (this.id === 'calendar') return 'Calendar'
            if (this.id === 'linked_accounts') return 'Account switcher'
            if (this.id === 'actions') return 'Smart tasks'
            return data.name ?? undefined
        })()

        this.seqnum = this.propertyFromIntArg(data.seqnum)
        this.memo = (() => {
            if (this.id === 'overview') return 'See what PocketSuite can do for your business!'
            if (this.id === 'setup')
                return 'Take the first steps to run your business on PocketSuite'
            if (this.id === 'reports')
                return 'Dig into your income, bookings, capacity utilization, and more'
            if (this.id === 'imports') return 'Bring exisiting contracts into PocketSuite'
            if (this.id === 'fields')
                return 'Collect client, booking, and sub-account information during checkouts'
            if (this.id === 'documents')
                return 'Collect documents and images from clients and set expiration dates'
            if (this.id === 'cancellations')
                return 'Automatically enforce a cancellation policy on bookings, subscriptions, and orders'
            if (this.id === 'conference')
                return 'Use PocketSuite video conferencing or Zoom for virtual appointments'
            if (this.id === 'calendar') return 'Setup and manage your schedule and bookings'
            if (this.id === 'actions') return 'Create automated task schedules tied to appointments'
            if (this.id === 'linked_accounts')
                return 'Create and manage multiple PocketSuite accounts from the same login number'
            const memo = data.memo ?? undefined
            if (memo && this.id === 'subaccount') {
                const type = PSApplication.me()!.owner!.industry!.subaccountType
                return memo.replace(
                    '{subaccounts}',
                    Subaccount.labelString(type, true).toLowerCase()
                )
            }
            return memo
        })()

        this.pitch = (() => {
            let pitch = data.pitch ?? undefined
            if (pitch === undefined) {
                return undefined
            }

            if (this.id === 'subaccount') {
                const type = PSApplication.me()!.owner!.industry!.subaccountType
                pitch = pitch.replace(
                    '{subaccounts}',
                    Subaccount.labelString(type, true).toLowerCase()
                )
            }
            return pitch
        })()
        this.parent = data.parent ?? undefined
        this.requiresPlus = data.requires_plus ?? false
        this.requiresPremium = data.requires_premium ?? false
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<FeatureConstructorArgs>): Feature {
        return new Feature({ ...this.serializeForClone(), ...overrides })
    }

    static initWithId(id: string): Feature {
        const feature = CacheFactory.getInstance().getRecord(Feature, id) ?? new Feature({ id })
        return feature
    }

    get iconSize(): 'large' | 'medium' | 'small' | 'payment' {
        return 'large'
    }

    iconStringForOwner(owner: User): IconNameAndCategoryString {
        return `${this.iconForOwner(owner)}.${this.iconSize}`
    }

    iconForOwner(owner: User): IconName {
        switch (this.id) {
            case 'overview':
                return 'pocketsuite'
            case 'setup':
                return 'rocket'
            case 'conference':
                return 'video'
            case 'reports':
                return 'graph'
            case 'calendar':
                return 'calendar'
            case 'fields':
                return 'notepad'
            case 'cancellations':
                return 'close2'
            case 'resource':
                return 'tool'
            case 'documents':
                return 'usersDocument'
            case 'group':
                return 'messages'
            case 'actions':
                return 'notepadCheckmark'
            case 'template':
                return 'envelope'
            case 'reminder':
                return 'alarmClock'
            case 'class':
                return 'student'
            case 'field':
                return 'form'
            case 'package':
                return 'package'
            case 'estimate':
                return 'dollar4'
            case 'subscription':
                return 'refresh'
            case 'contract':
                return 'pen'
            case 'imports':
                return 'phoneArrowDown'
            case 'link':
                return 'monitor'
            case 'widget':
                return 'computerClock'
            case 'record':
                return 'field'
            case 'discount':
                return 'discount'
            case 'salestax':
                return 'percentage2'
            case 'lead':
                return 'computerCheckmark'
            case 'chat':
                return 'message'
            case 'surcharge':
                return 'percentage3'
            case 'staffing':
                return 'employeeCard'
            case 'reservation':
                return 'calendarClock'
            case 'services':
                return 'tags'
            case 'recurring':
                return 'repeat'
            case 'gratuity':
                return 'money'
            case 'product':
                return 'package'
            case 'itemize':
                return 'list'
            case 'invoice':
                return 'documentClock'
            case 'task':
                return 'notepadCheckmark'
            case 'gift':
                return 'gift'
            case 'billing':
                return 'documentClock'
            case 'drip':
                return 'envelopeClock'
            case 'booking':
                return 'calendarClock'
            case 'payment':
                return 'computerDollar'
            case 'reader':
                return 'cardReader'
            case 'benchmark':
                return 'trophy'
            case 'location':
                return 'location'
            case 'paypal':
                return 'computerDollar'
            case 'category':
                return 'grid'
            case 'trigger':
                return 'flag'
            case 'mark':
                return 'coinsHand'
            case 'bnpl':
                return 'calendarDollar'
            case 'marketing':
                return 'google'
            case 'linked_accounts':
                return 'briefcase'
            case 'subaccount':
                return Subaccount.iconNameForSubaccountType(
                    owner.industry?.subaccountType ?? 'none'
                )
        }

        return 'info'
    }

    get key(): string {
        return (
            'allow' +
            (this.id === 'bnpl'
                ? this.id!.toUpperCase()
                : this.id!.charAt(0).toUpperCase() + this.id!.slice(1))
        )
    }

    // TODO: Implement PSApplication.getInstance().getString
    get tutorial(): string {
        if (
            ['overview', 'setup', 'reports', 'fields', 'documents'].includes(this.id!) ||
            this.feature === undefined
        )
            return this.id!
        switch (this.feature) {
            case FEATURE.FEATURE_ESTIMATE:
                return 'estimates'
            case FEATURE.FEATURE_LINK:
                return 'link'
            case FEATURE.FEATURE_CHAT:
                return 'chat'
            case FEATURE.FEATURE_LEAD:
                return 'leads'
            case FEATURE.FEATURE_PACKAGE:
                return 'packages'
            case FEATURE.FEATURE_GIFT:
                return 'gifts'
            case FEATURE.FEATURE_CLASS:
                return 'classes'
            case FEATURE.FEATURE_SUBSCRIPTION:
                return 'subscriptions'
            case FEATURE.FEATURE_RECORD:
                return 'forms'
            case FEATURE.FEATURE_CONTRACT:
                return 'contracts'
            case FEATURE.FEATURE_WIDGET:
                return 'widget'
            case FEATURE.FEATURE_STAFFING:
                return 'employees'
            case FEATURE.FEATURE_TEMPLATE:
                return 'templates'
            case FEATURE.FEATURE_SUBACCOUNT:
                return 'subaccounts'
        }
        return this.id!
    }

    get feature(): FEATURE | undefined {
        switch (this.id) {
            case 'class':
                return FEATURE.FEATURE_CLASS
            case 'package':
                return FEATURE.FEATURE_PACKAGE
            case 'gift':
                return FEATURE.FEATURE_GIFT
            case 'estimate':
                return FEATURE.FEATURE_ESTIMATE
            case 'subscription':
                return FEATURE.FEATURE_SUBSCRIPTION
            case 'billing':
                return FEATURE.FEATURE_BILLING
            case 'task':
                return FEATURE.FEATURE_TASK
            case 'drip':
                return FEATURE.FEATURE_DRIP
            case 'post':
                return FEATURE.FEATURE_POST
            case 'rollup':
                return FEATURE.FEATURE_ROLLUP
            case 'royalty':
                return FEATURE.FEATURE_ROYALTY
            case 'reminder':
                return FEATURE.FEATURE_REMINDER
            case 'contract':
                return FEATURE.FEATURE_CONTRACT
            case 'widget':
                return FEATURE.FEATURE_WIDGET
            case 'record':
                return FEATURE.FEATURE_RECORD
            case 'field':
                return FEATURE.FEATURE_FIELD
            case 'discount':
                return FEATURE.FEATURE_DISCOUNT
            case 'salestax':
                return FEATURE.FEATURE_SALESTAX
            case 'lead':
                return FEATURE.FEATURE_LEAD
            case 'marketing':
                return FEATURE.FEATURE_MARKETING
            case 'link':
                return FEATURE.FEATURE_LINK
            case 'chat':
                return FEATURE.FEATURE_CHAT
            case 'surcharge':
                return FEATURE.FEATURE_SURCHARGE
            case 'staffing':
                return FEATURE.FEATURE_STAFFING
            case 'reservation':
                return FEATURE.FEATURE_RESERVATION
            case 'services':
                return FEATURE.FEATURE_SERVICES
            case 'recurring':
                return FEATURE.FEATURE_RECURRING
            case 'gratuity':
                return FEATURE.FEATURE_GRATUITY
            case 'group':
                return FEATURE.FEATURE_GROUP
            case 'template':
                return FEATURE.FEATURE_TEMPLATE
            case 'product':
                return FEATURE.FEATURE_PRODUCT
            case 'itemize':
                return FEATURE.FEATURE_ITEMIZE
            case 'reader':
                return FEATURE.FEATURE_READER
            case 'payment':
                return FEATURE.FEATURE_PAYMENT
            case 'booking':
                return FEATURE.FEATURE_BOOKING
            case 'invoice':
                return FEATURE.FEATURE_INVOICE
            case 'benchmark':
                return FEATURE.FEATURE_BENCHMARK
            case 'paypal':
                return FEATURE.FEATURE_PAYPAL
            case 'category':
                return FEATURE.FEATURE_CATEGORY
            case 'trigger':
                return FEATURE.FEATURE_TRIGGER
            case 'subaccount':
                return FEATURE.FEATURE_SUBACCOUNT
            case 'bnpl':
                return FEATURE.FEATURE_BNPL
            case 'mark':
                return FEATURE.FEATURE_MARK
            case 'resource':
                return FEATURE.FEATURE_RESOURCE
        }
        return undefined
    }

    get requiredPlan(): string | undefined {
        return this.requiresPlus ? 'plus' : this.requiresPremium ? 'premium' : undefined
    }

    requiresUpgrade(account: User): boolean {
        if ('premium' === this.requiredPlan) return !account.plan
        else if ('plus' === this.requiredPlan) return !account.plan || 'premium' === account.plan
        else if ('suite' === this.requiredPlan)
            return !account.plan || 'premium' === account.plan || 'plus' === account.plan
        return false
    }

    allowed(industry: Industry): boolean {
        return Feature.allowed(this.feature!, industry)
    }

    static enabled(feature: FEATURE | undefined, account: User): boolean {
        if (feature === undefined) return true
        if (!Feature.allowed(feature, account.industry)) return false
        switch (feature) {
            case FEATURE.FEATURE_GROUP:
                return !!account.allowGroup
            case FEATURE.FEATURE_TEMPLATE:
                return !!account.allowTemplate
            case FEATURE.FEATURE_CLASS:
                return !!account.allowClass
            case FEATURE.FEATURE_PACKAGE:
                return !!account.allowPackage
            case FEATURE.FEATURE_ESTIMATE:
                return !!account.allowEstimate
            case FEATURE.FEATURE_SUBSCRIPTION:
                return !!account.allowSubscription
            case FEATURE.FEATURE_BILLING:
                return !!account.allowBilling
            case FEATURE.FEATURE_CONTRACT:
                return !!account.allowContract
            case FEATURE.FEATURE_WIDGET:
                return !!account.allowWidget
            case FEATURE.FEATURE_RECORD:
                return !!account.allowRecord
            case FEATURE.FEATURE_FIELD:
                return !!(
                    account.allowLead ||
                    account.allowWidget ||
                    account.allowSubaccount ||
                    account.allowProduct
                )
            case FEATURE.FEATURE_DISCOUNT:
                return !!account.allowDiscount
            case FEATURE.FEATURE_SALESTAX:
                return !!account.allowSalesTax
            case FEATURE.FEATURE_LEAD:
                return !!account.allowLead
            case FEATURE.FEATURE_MARKETING:
                return !!account.allowMarketing
            case FEATURE.FEATURE_LINK:
                return !!account.allowLink
            case FEATURE.FEATURE_CHAT:
                return !!account.allowChat
            case FEATURE.FEATURE_TASK:
                return !!account.allowTask
            case FEATURE.FEATURE_GIFT:
                return !!account.allowGift
            case FEATURE.FEATURE_DRIP:
                return !!account.allowDrip
            case FEATURE.FEATURE_POST:
                return !!account.allowPost
            case FEATURE.FEATURE_REMINDER:
                return !!account.allowReminder
            case FEATURE.FEATURE_SURCHARGE:
                return !!account.allowSurcharge
            case FEATURE.FEATURE_STAFFING:
                return !!account.allowStaffing
            case FEATURE.FEATURE_RESERVATION:
                return !!account.allowReservation
            case FEATURE.FEATURE_SERVICES:
                return !!account.allowServices
            case FEATURE.FEATURE_RECURRING:
                return !!account.allowRecurring
            case FEATURE.FEATURE_GRATUITY:
                return !!account.allowGratuity
            case FEATURE.FEATURE_PRODUCT:
                return !!account.allowProduct
            case FEATURE.FEATURE_ITEMIZE:
                return !!account.allowItemize
            case FEATURE.FEATURE_BOOKING:
                return !!account.allowBooking
            case FEATURE.FEATURE_PAYMENT:
                return !!account.allowPayment
            case FEATURE.FEATURE_READER:
                return !!account.allowReader
            case FEATURE.FEATURE_INVOICE:
                return !!account.allowInvoice
            case FEATURE.FEATURE_BENCHMARK:
                return !!account.allowBenchmark
            case FEATURE.FEATURE_PAYPAL:
                return !!account.allowPaypal
            case FEATURE.FEATURE_CATEGORY:
                return !!account.allowCategory
            case FEATURE.FEATURE_TRIGGER:
                return !!account.allowTrigger
            case FEATURE.FEATURE_SUBACCOUNT:
                return !!account.allowSubaccount
            case FEATURE.FEATURE_MARK:
                return !!account.allowMark
            case FEATURE.FEATURE_BNPL:
                return !!account.allowBNPL
            case FEATURE.FEATURE_ROLLUP:
                return !!account.allowRollup
            case FEATURE.FEATURE_ROYALTY:
                return !!account.allowRoyalty
            case FEATURE.FEATURE_RESOURCE:
                return !!account.allowResource
            default:
                return false
        }
    }

    static allowed(feature: FEATURE, industry: Industry | undefined): boolean {
        if (industry === undefined) return false
        switch (feature) {
            case FEATURE.FEATURE_CLASS:
                return !!industry.classes
            case FEATURE.FEATURE_ESTIMATE:
                return !!industry.estimate
            case FEATURE.FEATURE_RESERVATION:
                return !!industry.reservations
            case FEATURE.FEATURE_SERVICES:
                return !!industry.services
            case FEATURE.FEATURE_RECURRING:
                return !!industry.recurring
            case FEATURE.FEATURE_GRATUITY:
                return !!industry.gratuity
            case FEATURE.FEATURE_PRODUCT:
                return !!industry.product
            default:
                return true
        }
    }

    deserialize(data: any) {
        super.deserialize(data)

        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {}

    static dataType(): DataType {
        return 'features'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict.name = this.name
        return dict
    }
}
